import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { Course, Topic } from './appInterfaceDataSlice';
import { AppUserSubscriptionRecord } from './appStateSlice';
import type { RootState } from '../store';

// Interface
export interface ShipmentDetail {
  grade: Course['grade'];
  targetDispatchDate: string;
  targetDeliveryDate: string;
}

export interface SFCarrierResponse {
  fullRoute: {
    mailNo: string;
    routes: SFRoute[];
  };
  latestRoute: SFRoute | undefined;
  pickupRoute: SFRoute | undefined;
}

interface SFRoute {
  opCode: string; // SF RESPONSE OPERATION CODE: https://open.sf-express.com/developSupport/734349?activeIndex=589678
  remark: string;
  acceptTime: string;
  acceptAddress: string;
}
export interface ShipmentRecord {
  id: string;
  userId: string;
  childProfileId: string;
  subscriptionId: string;
  trackNumber: string;
  carrier: 'SF' | 'OTHER';
  address: string;
  shipmentDetail: ShipmentDetail;
  carrierResponse: SFCarrierResponse; // MIGHT INCLUDE OTHER CARRIER LATER
  topics: Topic[];
  createdAt: string;
  updatedAt: string;
}

// Define a type for the slice state
export interface AppParentPortalDataSlice {
  selectedSubscription: AppUserSubscriptionRecord | undefined;
  shipmentRecord: ShipmentRecord[] | undefined;
  paymentRecord:
    | {
        payment_id: string;
        user_id: string;
        payment_detail: Record<string, unknown>;
        payment_status: string;
        payment_response: Record<string, unknown>;
        create_time: string;
        update_time: string;
      }
    | undefined;
  subscriptionRecord: Record<string, unknown> | undefined;
}

// Define the initial state using that type
const initialState: AppParentPortalDataSlice = {
  selectedSubscription: undefined,
  shipmentRecord: undefined,
  paymentRecord: undefined,
  subscriptionRecord: undefined,
};

export const appParentPortalDataSlice = createSlice({
  name: 'appParentPortalData',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setAppParentPortalData(
      state,
      action: PayloadAction<{
        dataKey: keyof AppParentPortalDataSlice;
        data: unknown;
      }>,
    ): void {
      // @ts-expect-error: Stupid TS checking
      state[action.payload.dataKey] = action.payload.data;
    },
  },
});

export const { setAppParentPortalData } = appParentPortalDataSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectAppParentPortalData = (
  state: RootState,
): AppParentPortalDataSlice => {
  return state.appParentPortalData;
};

export default appParentPortalDataSlice.reducer;
